/**
 * ReactSelectFormField Component
 *
 * A controlled select form field component that integrates with react-hook-form and provides
 * validation, error handling, tooltips, and support for single and multi-select options.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.id=''] - Unique identifier for the select field
 * @param {string} [props.name=''] - Field name for form control
 * @param {string} [props.label=''] - Label text displayed above the field
 * @param {Object} [props.control=null] - React Hook Form control object
 * @param {Object} [props.rules={}] - React Hook Form validation rules
 * @param {boolean} [props.isMulti=false] - Enable multi-select functionality
 * @param {string} [props.placeholder=''] - Placeholder text for the select
 * @param {Function} [props.onChange=null] - Custom onChange handler; defaults to setValue with trigger
 * @param {Function} [props.trigger=() => {}] - React Hook Form trigger function for validation
 * @param {Function} [props.setValue=() => {}] - React Hook Form setValue function
 * @param {Function} [props.setError=() => {}] - React Hook Form setError function
 * @param {*} [props.defaultValue=null] - Default selected value(s)
 * @param {*} [props.value=null] - Current selected value(s)
 * @param {string} [props.apiUrl=''] - API endpoint URL for fetching select options
 * @param {boolean} [props.error=false] - Error state flag
 * @param {string} [props.errorMessage=''] - Error message to display
 * @param {string} [props.hint=''] - Tooltip hint text displayed next to label
 * @param {Object} [props.restProps] - Additional props passed to ReactSelect component
 *
 * @returns {JSX.Element} Rendered select form field with label, tooltip, and error alert
 *
 * @example
 * <ReactSelectFormField
 *   id="country"
 *   name="country"
 *   label="Select Country"
 *   control={control}
 *   rules={{ required: 'Country is required' }}
 *   placeholder="Choose a country..."
 *   hint="Select your country of residence"
 *   error={!!errors.country}
 *   errorMessage={errors.country?.message}
 *   setValue={setValue}
 *   trigger={trigger}
 *   apiUrl="/api/countries"
 * />
 */

import {
  ComponentLevelNotification as Alert,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@americanexpress/dls-react'
import { IconInfo } from '@americanexpress/dls-icons'
import { Controller } from 'react-hook-form'
import ReactSelect from './ReactSelect'
import styles from './scss/ReactSelectFormField.module.scss'

export default function ReactSelectFormField({
  id = '',
  name = '',
  label = '',
  control = null,
  rules = {},
  isMulti = false,
  placeholder = '',
  onChange = null,
  trigger = () => {},
  setValue = () => {},
  setError = () => {},
  defaultValue = null,
  value = null,
  apiUrl = '',
  error = false,
  errorMessage = '',
  hint = '',
  ...restProps
}) {
  return (
    <>
      {label && (
        <Label htmlFor={id}>
          {label}
          {hint && (
            <Tooltip id={`tooltip-hint-${id}`} className={styles.tooltipWrapper}>
              <TooltipTrigger>
                <IconInfo titleId='tooltip-hint-icon' />
              </TooltipTrigger>
              <TooltipContent>{hint}</TooltipContent>
            </Tooltip>
          )}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <>
            <ReactSelect
              {...field}
              id={id}
              name={name}
              isMulti={isMulti}
              placeholder={placeholder}
              onBlur={() => trigger(name)}
              onChange={
                onChange ||
                (changedValue => {
                  setValue(name, changedValue)
                  trigger(name)
                })
              }
              defaultValue={defaultValue}
              value={value}
              error={error}
              apiUrl={apiUrl}
              setError={setError}
              {...restProps}
            />
            {error && (
              <Alert id='input-primary-error-alert' status='error'>
                {errorMessage}
              </Alert>
            )}
          </>
        )}
      />
    </>
  )
}
